import { useState } from "react";
import type { Notification } from "../../types";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { postRequest } from "@/utils/apiHelper";

dayjs.extend(relativeTime);

interface LeadSelfAssignProps {
  notification: Notification;
  updateNotificationStatus?: (params: {
    notification?: any;
    redirect?: boolean;
    otherSwitch?: boolean;
  }) => void;
}

export const LeadSelfAssign = ({
  notification,
  updateNotificationStatus,
}: LeadSelfAssignProps) => {
  const [claiming, setClaiming] = useState(false);

  // Parse notification content to get lead count
  let leadCount = 100;
  let message = "Your leads are ready to be claimed.";

  try {
    const content = notification.content
      ? JSON.parse(notification.content)
      : {};
    leadCount = content.lead_count || 100;
    message = content.message || message;
  } catch (e) {
    console.error("Failed to parse notification content:", e);
  }

  const handleMarkAsRead = () => {
    if (updateNotificationStatus) {
      updateNotificationStatus({
        notification: notification,
      });
    }
  };

  const handleClaimLeads = async () => {
    setClaiming(true);

    try {
      // @ts-ignore - postRequest accepts optional parameters
      const response = await postRequest("notification/claim-leads", {
        notification_id: notification.id,
      });

      if (response?.data?.success) {
        const distributed = response.data.distributed || leadCount;
        toast.success(`Successfully claimed ${distributed} leads!`);

        // Mark notification as read
        if (updateNotificationStatus) {
          updateNotificationStatus({
            notification: notification,
          });
        }
      } else {
        const errorMsg = response?.data?.message || "Failed to claim leads";
        toast.error(errorMsg);
      }
    } catch (error: any) {
      console.error("Error claiming leads:", error);
      const errorMsg =
        error?.message || "Failed to claim leads. Please try again.";
      toast.error(errorMsg);
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div className="bg-primary/10 p-2 rounded-md w-full relative">
      <h2 className="text-sm font-medium">Lead Distribution Available</h2>
      <p className="text-xs text-muted-foreground mt-1">
        {message} Click below to receive your {leadCount} leads.
      </p>
      <time
        className="text-xs absolute top-2 right-2"
        dateTime={notification.notification_time}
      >
        {dayjs(notification.notification_time).fromNow()}
      </time>
      <div className="mt-2">
        <Button
          size="sm"
          onClick={handleClaimLeads}
          disabled={claiming || Boolean(notification.read_status)}
        >
          {claiming ? "Claiming..." : `Claim ${leadCount} Leads`}
        </Button>
        {!notification.read_status && (
          <Button
            size="sm"
            variant="outline"
            className="ml-2"
            onClick={handleMarkAsRead}
            disabled={claiming}
          >
            Mark as read
          </Button>
        )}
        {notification.read_status === 1 && (
          <span className="ml-2 text-xs text-muted-foreground">
            Already claimed
          </span>
        )}
      </div>
    </div>
  );
};
